"use client";

import * as React from "react";
import { Home, User, Menu, X, LogOut, Calendar } from "lucide-react";
import { API_ENDPOINTS } from "../constants/config";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { clearStoredAuth, getToken } from "../utils/authStorage";
import { useAccessBlock } from "../hooks/useAccessBlock";
import AccessBlockOverlay from "../components/schoolAdmin/Billing/AccessBlockOverlay";

export function StaffSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname.split("/")[2] || "profile";
  const [user, setUser] = React.useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const { blocked: accessBlocked, reason: accessBlockReason, supportPhone } = useAccessBlock();
  const showAccessBlock = accessBlocked && accessBlockReason === "DEACTIVATED";

  React.useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const token = getToken();
        if (!token) {
          window.location.href = "/login";
          return;
        }
        const response = await fetch(API_ENDPOINTS.usersProfile, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (response.status === 401) {
          clearStoredAuth();
          window.location.href = "/login";
          return;
        }
        if (!response.ok) throw new Error("Failed to fetch profile");
        const data = await response.json();
        if (data.status === "success" && data.data?.user) {
          const userData = {
            ...data.data.user,
            id: data.data.user._id || data.data.user.id,
          };
          setUser(userData);
          localStorage.setItem("user", JSON.stringify(userData));
        } else {
          const stored = localStorage.getItem("user");
          if (stored) setUser(JSON.parse(stored));
        }
      } catch (err) {
        console.error(err);
        const stored = localStorage.getItem("user");
        if (stored) setUser(JSON.parse(stored));
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, []);

  // Assigned tabs section (based on permissions - can be populated later)
  const assignedTabs = [
    // Add tabs here based on staff permissions
    // Example: { id: "attendance", label: "Attendance", icon: <Users className="w-5 h-5" />, path: "/dashboard/attendance" },
  ];

  // My Tabs section (always visible for staff)
  const myTabs = [
    { id: "profile", label: "My Profile", icon: <User className="w-5 h-5" />, path: "/dashboard/profile" },
    { id: "leave", label: "Leave Management", icon: <Calendar className="w-5 h-5" />, path: "/dashboard/leave" },
  ];

  const getFullName = (u) => {
    if (!u) return "Staff";
    return [u.firstName, u.middleName, u.lastName].filter(Boolean).join(" ") || "Staff";
  };
  const getInitials = (u) => {
    if (!u?.firstName) return "S";
    const f = u.firstName.charAt(0);
    const l = u.lastName ? u.lastName.charAt(0) : "";
    return (f + l).toUpperCase();
  };

  if (loading && !user) {
    return (
      <div className="flex min-h-screen bg-gray-50 items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="flex h-full bg-gray-50 relative overflow-hidden">
      <Button
        variant="ghost"
        className="lg:hidden fixed top-4 left-4 z-50 p-2"
        onClick={toggleSidebar}
      >
        {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </Button>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 w-[280px] lg:w-72
          bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} z-40
        `}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-gray-200">
            <button
              onClick={() => {
                navigate("/dashboard/profile");
                if (window.innerWidth < 1024) setIsSidebarOpen(false);
              }}
              className="flex items-center gap-3 w-full hover:opacity-80 transition-opacity cursor-pointer"
            >
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.profileImage || ""} alt={getFullName(user)} />
                <AvatarFallback>{getInitials(user)}</AvatarFallback>
              </Avatar>
              <div className="text-left">
                <h1 className="text-lg font-bold text-gray-800">{getFullName(user)}</h1>
                <p className="text-sm text-gray-500">Staff</p>
              </div>
            </button>
          </div>
          <nav className="flex-1 p-4 overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
            {/* My Tabs Section - Always Visible */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3">
                My Tabs
              </h3>
              <div className="space-y-1">
                {myTabs.map((item) => (
                  <Button
                    key={item.id}
                    variant="ghost"
                    className={`w-full justify-start text-sm mb-1 min-h-[44px] ${
                      currentTab === item.id ? "bg-[#0263cc] text-white" : "text-gray-600 hover:text-white hover:bg-[#0263cc]"
                    }`}
                    onClick={() => {
                      navigate(item.path);
                      if (window.innerWidth < 1024) setIsSidebarOpen(false);
                    }}
                  >
                    {item.icon}
                    <span className="ml-3">{item.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Assigned Tabs Section - Based on Permissions */}
            {assignedTabs.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3">
                  Assigned Tabs
                </h3>
                <div className="space-y-1">
                  {assignedTabs.map((item) => (
                    <Button
                      key={item.id}
                      variant="ghost"
                      className={`w-full justify-start text-sm mb-1 min-h-[44px] ${
                        currentTab === item.id ? "bg-[#0263cc] text-white" : "text-gray-600 hover:text-white hover:bg-[#0263cc]"
                      }`}
                      onClick={() => {
                        navigate(item.path);
                        if (window.innerWidth < 1024) setIsSidebarOpen(false);
                      }}
                    >
                      {item.icon}
                      <span className="ml-3">{item.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </nav>
          <div className="p-4 border-t border-gray-200">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px]"
              onClick={() => {
                clearStoredAuth();
                window.location.href = "/login";
              }}
            >
              <LogOut className="w-5 h-5" />
              <span className="ml-3">Logout</span>
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-auto lg:ml-0 w-full">
        <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
          {showAccessBlock ? (
            <AccessBlockOverlay
              visible={showAccessBlock}
              reason={accessBlockReason}
              showRenewButton={false}
              supportPhone={supportPhone}
            />
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}
